import { RequireAuth } from '@/components/require-auth'
import { AppLayout } from '@/components/app-layout'
import {
  AgencyService,
  PLAN_LABELS,
  type AgencyBillingState,
  type AgencyPlanInfo,
  type AgencyRole,
  type PlanType,
} from '@/services/agency-service'
import { ApiError } from '@/lib/api-client'
import { Button, Notice, TextInput } from '@tempots/beatui'
import {
  attr,
  computedOf,
  html,
  MapSignal,
  on,
  OnDispose,
  prop,
  Signal,
  When,
} from '@tempots/dom'
import { Anchor, HTMLTitle, NavigationService, Query } from '@tempots/ui'
import { EphimeralService } from '@/utils/ephimeral'

type SellablePlan = 'free' | 'standard' | 'pro' | 'team'

const PLAN_CARDS: Array<{
  plan: SellablePlan
  price: string
  blurb: string
}> = [
  {
    // The Free card doubles as the cancel path: cancelling stops charges,
    // never the account. It is unpublished on the marketing pricing page.
    plan: 'free',
    price: '$0',
    blurb:
      '5 resumes per month, 1 seat. Cancelling stops the charges — your account and everything you’ve made stay.',
  },
  { plan: 'standard', price: '$9/mo', blurb: 'Unlimited resumes' },
  { plan: 'pro', price: '$15/mo', blurb: 'Unlimited resumes + redaction' },
  {
    plan: 'team',
    price: '$19/user/mo',
    blurb:
      'Everything in Pro + your team on one shared template — billed per member',
  },
]

/** Whole days until an ISO timestamp, never negative. */
const daysUntil = (iso: string): number =>
  Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000))

/**
 * Billing v2: the four-tier plan picker backed by Stripe Checkout for new
 * subscriptions and subscription updates for switches. Agency (team) is
 * billed per member at $19/user/mo. Complimentary agencies (staff-comped)
 * see no plan cards. Owners only; members see the current plan read-only.
 */
export function BillingPage({ agencyId }: { agencyId: Signal<string> }) {
  return RequireAuth({}, () =>
    AppLayout({
      agencyId,
      content: html.div(
        HTMLTitle('Billing - ResumeHeaders'),
        attr.class('p-4 sm:p-8 h-full overflow-auto'),
        Query<
          string,
          {
            planInfo: AgencyPlanInfo
            myRole: AgencyRole | null
            billingState: AgencyBillingState
          }
        >({
          request: agencyId,
          load: async ({ request: id }) => {
            const [planInfo, myRole, billingState] = await Promise.all([
              AgencyService.getPlanInfo(id),
              AgencyService.getMyAgencyRole(id),
              AgencyService.getBillingState(id).catch(
                (): AgencyBillingState => ({
                  // billing:read is owner-only; members still get the page
                  available: false,
                  hasStripeCustomer: false,
                  billing: null,
                })
              ),
            ])
            return { planInfo, myRole, billingState }
          },
          keepOnReload: true,
          pending: () => html.div(attr.class('text-gray-500'), 'Loading…'),
          failure: ({ reload }) =>
            html.div(
              attr.class('flex flex-col gap-3 items-start'),
              Notice(
                { variant: 'danger', title: 'Could not load billing info' },
                'Something went wrong while loading your plan.'
              ),
              Button({ variant: 'outline', onClick: reload }, 'Try again')
            ),
          success: ({ value, reload }) => {
            const planInfo = value.$.planInfo
            const isOwner = value.$.myRole.map(role => role === 'owner')
            const billingState = value.$.billingState

            const busy = prop(false)
            const error = prop<string | null>(null)
            const errorCode = prop<string | null>(null)
            const info = prop<string | null>(null)

            // Checkout redirect results (?checkout=success|cancelled) and
            // the pricing-page plan intent (?intent=pro)
            const urlParams = new URLSearchParams(window.location.search)
            const checkoutResult = urlParams.get('checkout')
            if (
              checkoutResult === 'success' ||
              billingState.value.billing?.stripeSubscriptionId != null
            ) {
              // Intent fulfilled — stop reminding
              EphimeralService.remove('planIntent')
            }

            // ?checkout=success only proves Stripe redirected us back; the
            // plan is live once the webhook has stored the subscription.
            // Poll for it so the banner reports what actually happened
            // (billing:read is owner-only — anyone else keeps the old
            // trust-the-redirect banner rather than a poll that can't
            // succeed).
            const activation = prop<'waiting' | 'confirmed' | 'timeout'>(
              !billingState.value.available ||
                billingState.value.billing?.stripeSubscriptionId != null
                ? 'confirmed'
                : 'waiting'
            )
            let activationPoll: ReturnType<typeof setInterval> | undefined
            if (
              checkoutResult === 'success' &&
              activation.value === 'waiting'
            ) {
              let attempts = 0
              activationPoll = setInterval(() => {
                attempts += 1
                AgencyService.getBillingState(agencyId.value)
                  .then(state => {
                    if (state.billing?.stripeSubscriptionId != null) {
                      clearInterval(activationPoll)
                      activation.set('confirmed')
                      reload()
                    } else if (attempts >= 15) {
                      clearInterval(activationPoll)
                      activation.set('timeout')
                    }
                  })
                  .catch(() => {
                    if (attempts >= 15) {
                      clearInterval(activationPoll)
                      activation.set('timeout')
                    }
                  })
              }, 2000)
            }
            const intentParam = urlParams.get('intent')
            const planIntent =
              intentParam &&
              ['standard', 'pro', 'team'].includes(intentParam) &&
              billingState.value.available &&
              !billingState.value.billing?.stripeSubscriptionId
                ? (intentParam as SellablePlan)
                : null

            const run = async (action: () => Promise<void>) => {
              busy.set(true)
              error.set(null)
              errorCode.set(null)
              info.set(null)
              try {
                await action()
              } catch (err) {
                error.set(
                  err instanceof ApiError && err.message
                    ? err.message
                    : 'That did not go through. Please try again.'
                )
                errorCode.set(
                  err instanceof ApiError ? (err.code ?? null) : null
                )
              } finally {
                busy.set(false)
              }
            }

            const selectPlan = (target: SellablePlan) =>
              void run(async () => {
                const state = billingState.value

                if (!state.available) {
                  error.set('Payments are not available in this environment.')
                  return
                }

                if (state.billing?.stripeSubscriptionId) {
                  const result = await AgencyService.changeSubscription(
                    agencyId.value,
                    target
                  )
                  if (result.pendingCancellation) {
                    info.set(
                      'Your subscription will end at the close of the billing period; the agency then moves to Free.'
                    )
                  } else if (result.resumed) {
                    info.set('Your subscription will continue — welcome back.')
                  } else if (result.plan) {
                    info.set(
                      `Subscription changed — you're now on ${PLAN_LABELS[result.plan] ?? result.plan}. The price difference is prorated automatically on your next invoice.`
                    )
                  }
                  reload()
                } else if (target !== 'free') {
                  const { url } = await AgencyService.startCheckout(
                    agencyId.value,
                    target
                  )
                  window.location.href = url
                }
              })

            const openPortal = () =>
              void run(async () => {
                const { url } = await AgencyService.openBillingPortal(
                  agencyId.value
                )
                window.location.href = url
              })

            const currentIs = (plan: SellablePlan) =>
              planInfo.map(p => p.plan === plan)

            return html.div(
              attr.class('flex flex-col gap-6 max-w-4xl mx-auto'),
              OnDispose(() => clearInterval(activationPoll)),
              html.h1(attr.class('text-xl font-bold text-gray-800'), 'Billing'),
              checkoutResult === 'success'
                ? MapSignal(activation, state =>
                    state === 'confirmed'
                      ? Notice(
                          { variant: 'success', title: 'Subscription active' },
                          'Payment confirmed — your new plan is live.'
                        )
                      : state === 'waiting'
                        ? Notice(
                            { variant: 'info', title: 'Payment received' },
                            'Activating your plan — this usually takes a few seconds…'
                          )
                        : Notice(
                            { variant: 'warning', title: 'Payment received' },
                            'Activation is taking longer than usual. Refresh this page in a minute — if your plan still shows Free, contact us and we will sort it out.'
                          )
                  )
                : checkoutResult === 'cancelled'
                  ? Notice(
                      { variant: 'info' },
                      'Checkout was cancelled; your plan is unchanged.'
                    )
                  : null,
              planIntent != null
                ? When(isOwner, () =>
                    html.div(
                      attr.class(
                        'border-2 border-teal-600 bg-teal-50/40 rounded-lg p-4 flex flex-col gap-2'
                      ),
                      html.div(
                        attr.class('font-semibold text-gray-800'),
                        `Finish setting up ${PLAN_LABELS[planIntent as PlanType]}`
                      ),
                      html.div(
                        attr.class('text-sm text-gray-600'),
                        PLAN_CARDS.find(card => card.plan === planIntent)
                          ?.blurb ?? ''
                      ),
                      html.div(
                        attr.class('flex items-center gap-4 flex-wrap mt-1'),
                        Button(
                          {
                            variant: 'filled',
                            color: 'primary',
                            disabled: busy,
                            loading: busy,
                            onClick: () => selectPlan(planIntent),
                          },
                          `Continue to checkout — ${
                            PLAN_CARDS.find(card => card.plan === planIntent)
                              ?.price ?? ''
                          }`
                        ),
                        html.button(
                          attr.type('button'),
                          attr.class(
                            'text-sm text-gray-500 hover:text-gray-700 hover:underline'
                          ),
                          on.click(() => {
                            // Skip path: back to the workspace on free; the
                            // workspace shows a dismissable reminder chip
                            // while the intent is still stored.
                            NavigationService.navigate(
                              `/${agencyId.value}/workspace`
                            )
                          }),
                          'Skip for now — you’re on your free trial'
                        )
                      )
                    )
                  )
                : null,
              html.div(
                attr.class('text-sm font-semibold text-gray-800'),
                computedOf(
                  planInfo,
                  isOwner
                )((p, owner) => {
                  if (p.complimentary) {
                    return `Current plan: Agency (complimentary) — ${p.currentUsers} members, arranged with ResumeHeaders`
                  }
                  if (p.plan === 'trial' && p.trialEndsAt) {
                    const days = daysUntil(p.trialEndsAt)
                    return `Current plan: Free trial — ${days} day${days === 1 ? '' : 's'} left (ends ${new Date(p.trialEndsAt).toLocaleDateString()}). Unlimited resumes and seats until then.`
                  }
                  if (p.plan === 'team' && p.maxUsers != null) {
                    // Pricing is the owner's business — members just see the roster size
                    return owner
                      ? `Current plan: Agency — ${p.maxUsers} seat${p.maxUsers === 1 ? '' : 's'} × $19 = $${p.maxUsers * 19}/mo (${p.currentUsers} filled)`
                      : `Current plan: Agency — ${p.maxUsers} seat${p.maxUsers === 1 ? '' : 's'} (${p.currentUsers} filled)`
                  }
                  const seats =
                    p.maxUsers == null
                      ? `${p.currentUsers} seats used`
                      : `${p.currentUsers} of ${p.maxUsers} seats used`
                  return `Current plan: ${PLAN_LABELS[p.plan] ?? p.plan} — ${seats}`
                })
              ),
              When(
                planInfo.map(p => p.plan === 'trial'),
                () =>
                  Notice(
                    { variant: 'info', title: 'You’re on your free trial' },
                    'Pick a plan before your trial ends to keep unlimited resumes, redaction, and your team — you won’t be charged until you choose.'
                  )
              ),
              When(
                planInfo.map(p => p.trialExpired === true && p.plan === 'free'),
                () =>
                  Notice(
                    {
                      variant: 'warning',
                      tone: 'prominent',
                      title: 'Your 14-day trial has ended',
                    },
                    'Your agency is on the free tier — 5 resumes per month with a single seat. Everything you’ve made is safe. Pick a plan to get unlimited access back.'
                  )
              ),
              When(
                billingState.map(b => b.billing?.cancelAtPeriodEnd === true),
                () =>
                  Notice(
                    {
                      variant: 'warning',
                      tone: 'prominent',
                      title: 'Subscription ending',
                    },
                    html.div(
                      attr.class('flex items-center gap-3 flex-wrap'),
                      html.span(
                        billingState.map(b => {
                          const when =
                            b.billing?.cancelAt ?? b.billing?.currentPeriodEnd
                          return when
                            ? `Your subscription is set to cancel on ${new Date(when).toLocaleDateString()} — after that, your agency drops to the Free plan.`
                            : 'Your subscription is set to cancel at the end of the billing period — after that, your agency drops to the Free plan.'
                        })
                      ),
                      Button(
                        {
                          size: 'sm',
                          variant: 'filled',
                          color: 'primary',
                          loading: busy,
                          disabled: busy,
                          onClick: () =>
                            selectPlan(planInfo.value.plan as SellablePlan),
                        },
                        'Keep my subscription'
                      )
                    )
                  )
              ),
              When(
                computedOf(
                  isOwner,
                  planInfo
                )((owner, p) => owner && p.complimentary === true),
                () =>
                  Notice(
                    { variant: 'info' },
                    'Your agency is on a complimentary arrangement — no billing applies. Contact ResumeHeaders to change plans.'
                  )
              ),
              When(
                computedOf(
                  isOwner,
                  planInfo
                )((owner, p) => owner && p.complimentary !== true),
                () =>
                  html.div(
                    attr.class(
                      'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'
                    ),
                    ...PLAN_CARDS.map(card =>
                      html.div(
                        attr.class(
                          computedOf(currentIs(card.plan))((current): string =>
                            current
                              ? 'border-2 border-teal-600 rounded-lg p-4 flex flex-col gap-2 bg-teal-50/30'
                              : 'border border-gray-200 rounded-lg p-4 flex flex-col gap-2'
                          )
                        ),
                        html.div(
                          attr.class('font-semibold text-gray-800'),
                          PLAN_LABELS[card.plan as PlanType] ?? card.plan
                        ),
                        html.div(
                          attr.class('text-xl font-bold text-gray-900'),
                          card.price
                        ),
                        html.div(
                          attr.class('text-xs text-gray-600 grow'),
                          card.blurb
                        ),
                        When(
                          currentIs(card.plan),
                          () =>
                            html.div(
                              attr.class('flex flex-col gap-1.5'),
                              html.div(
                                attr.class(
                                  'text-xs font-semibold text-teal-700 py-1.5'
                                ),
                                'Current plan'
                              ),
                              When(
                                billingState.map(
                                  b => b.billing?.cancelAtPeriodEnd === true
                                ),
                                () =>
                                  Button(
                                    {
                                      size: 'sm',
                                      variant: 'filled',
                                      color: 'primary',
                                      loading: busy,
                                      disabled: busy,
                                      onClick: () => selectPlan(card.plan),
                                    },
                                    'Keep subscription'
                                  )
                              )
                            ),
                          () =>
                            card.plan === 'free'
                              ? // The Free card is the cancel path, not a sale:
                                // "Cancel subscription" when one exists, an
                                // explanatory caption otherwise.
                                MapSignal(
                                  computedOf(
                                    planInfo,
                                    billingState
                                  )((p, b): 'cancel' | 'trial' | 'caption' =>
                                    b.billing?.stripeSubscriptionId != null
                                      ? 'cancel'
                                      : p.plan === 'trial'
                                        ? 'trial'
                                        : 'caption'
                                  ),
                                  state =>
                                    state === 'cancel'
                                      ? Button(
                                          {
                                            size: 'sm',
                                            variant: 'outline',
                                            color: 'primary',
                                            disabled: busy,
                                            loading: busy,
                                            onClick: () => selectPlan('free'),
                                          },
                                          'Cancel subscription'
                                        )
                                      : html.div(
                                          attr.class(
                                            'text-xs text-gray-500 py-1.5'
                                          ),
                                          state === 'trial'
                                            ? 'Where you’ll land if your trial ends'
                                            : 'Included with every account'
                                        )
                                )
                              : Button(
                                  {
                                    size: 'sm',
                                    variant: 'filled',
                                    color: 'primary',
                                    disabled: busy,
                                    loading: busy,
                                    onClick: () => selectPlan(card.plan),
                                  },
                                  'Choose'
                                )
                        )
                      )
                    )
                  )
              ),
              When(
                error.map(e => e != null),
                () =>
                  Notice(
                    { variant: 'danger' },
                    error.map(e => e ?? ''),
                    // Seat conflicts are resolved on the Agency page — hand
                    // the user the door instead of a dead-end message.
                    When(
                      errorCode.map(code => code === 'PLAN_HAS_MEMBERS'),
                      () =>
                        html.span(
                          ' Manage members on the ',
                          Anchor(
                            { href: agencyId.map(id => `/${id}/agency`) },
                            attr.class(
                              'text-teal-700 font-semibold hover:underline'
                            ),
                            'Agency page'
                          ),
                          '.'
                        )
                    )
                  )
              ),
              When(
                info.map(i => i != null),
                () =>
                  Notice(
                    { variant: 'info' },
                    info.map(i => i ?? '')
                  )
              ),
              When(
                computedOf(
                  isOwner,
                  planInfo,
                  billingState
                )(
                  (owner, p, b) =>
                    owner &&
                    p.plan === 'team' &&
                    p.complimentary !== true &&
                    b.billing?.stripeSubscriptionId != null
                ),
                () => {
                  const seatInput = prop(String(planInfo.value.maxUsers ?? 1))
                  const handleSeats = () => {
                    const parsed = Number(seatInput.value.trim())
                    if (!Number.isInteger(parsed) || parsed < 1) {
                      error.set('Seats must be a positive whole number')
                      return
                    }
                    if (parsed < planInfo.value.currentUsers) {
                      error.set(
                        `${planInfo.value.currentUsers} seats are filled — remove members on the Agency page before reducing below that`
                      )
                      return
                    }
                    void run(async () => {
                      const result = await AgencyService.setSeats(
                        agencyId.value,
                        parsed
                      )
                      info.set(
                        `You now have ${result.seats} seats ($${result.seats * 19}/mo) — ${result.filled} filled. Changes are prorated on your next invoice.`
                      )
                      reload()
                    })
                  }
                  return html.div(
                    attr.class(
                      'border border-gray-200 rounded-lg p-4 flex flex-col gap-2 max-w-md'
                    ),
                    html.div(
                      attr.class('font-semibold text-gray-800'),
                      'Seats'
                    ),
                    html.div(
                      attr.class('text-sm font-semibold text-gray-800'),
                      planInfo.map(
                        p =>
                          `${p.maxUsers ?? 1} purchased · ${p.currentUsers} filled`
                      )
                    ),
                    html.ul(
                      attr.class(
                        'text-sm text-gray-600 list-disc pl-5 flex flex-col gap-1'
                      ),
                      html.li(
                        'Buy more seats here — set the number and hit Update.'
                      ),
                      html.li(
                        'Assign seats by inviting people on the ',
                        Anchor(
                          { href: agencyId.map(id => `/${id}/agency`) },
                          attr.class(
                            'text-teal-700 font-medium hover:underline'
                          ),
                          'Agency page'
                        ),
                        '.'
                      ),
                      html.li(
                        'To reduce seats, first empty some by removing people on the Agency page.'
                      )
                    ),
                    html.div(
                      attr.class('flex items-end gap-2'),
                      html.div(
                        html.label(
                          attr.class(
                            'text-xs font-medium text-gray-500 block mb-1'
                          ),
                          'Seats'
                        ),
                        html.div(
                          attr.class('w-24'),
                          TextInput({
                            size: 'sm',
                            value: seatInput,
                            onInput: v => seatInput.set(v),
                          })
                        )
                      ),
                      Button(
                        {
                          size: 'sm',
                          variant: 'filled',
                          color: 'primary',
                          loading: busy,
                          disabled: busy,
                          onClick: handleSeats,
                        },
                        'Update seats'
                      )
                    )
                  )
                }
              ),
              When(
                computedOf(
                  isOwner,
                  billingState
                )((owner, b) => owner && b.hasStripeCustomer),
                () =>
                  html.div(
                    Button(
                      {
                        variant: 'outline',
                        size: 'sm',
                        disabled: busy,
                        onClick: openPortal,
                      },
                      'Manage payment methods & invoices'
                    )
                  )
              ),
              When(
                isOwner.map(owner => !owner),
                () =>
                  html.div(
                    attr.class('text-sm text-gray-500'),
                    'The agency owner ',
                    When(
                      planInfo.map(p => p.ownerEmail != null),
                      () =>
                        html.span(
                          '(',
                          html.a(
                            attr.class('text-teal-700 hover:underline'),
                            attr.href(
                              planInfo.map(p => `mailto:${p.ownerEmail ?? ''}`)
                            ),
                            planInfo.map(p => p.ownerEmail ?? '')
                          ),
                          ') '
                        )
                    ),
                    'manages the plan.'
                  )
              ),
              When(isOwner, () =>
                html.p(
                  attr.class('text-xs text-gray-400'),
                  'Downgrades that reduce seats require removing members first. ',
                  'Plan changes between paid tiers are prorated automatically.'
                )
              )
            )
          },
        })
      ),
    })
  )
}
